import { debugLog } from "../utils/debugLog.js";
import React, { Fragment, useMemo, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useUser } from '@clerk/clerk-react';
import Select from 'react-select';
import {
  ASSIGNEE_MENU_MIN_WIDTH,
  OPPORTUNITY_ASSIGNEES,
  buildAssigneeSelectOptions,
} from '../utils/opportunityAssignees.js';
import {
  mapStatusOptionsGroupedByColor,
  mapStatusOptionsWithColors,
} from '../utils/opportunityStatusOptions.js';
import {
  MODAL_SELECT_PROPS,
  buildOpportunityModalSelectStyles,
  buildOpportunityStatusSelectStyles,
} from '../utils/opportunitySelectStyles.js';
import {
  formatOpportunityStatusGroupLabel,
  formatOpportunityStatusOptionLabel,
} from '../utils/opportunityStatusSelectHelpers.jsx';
import { currencyFormat as formatCurrencyValue } from '../helpers.js';
import { fetchMitacsInstitutions } from '../utils/loadMitacsInstitutions.js';
import { countryCodes, regionCodes, industries } from '../helperData.js';
import ResumeQualificationPanel from './ResumeQualificationPanel.jsx';
import ProgramChecklistPreview from './ProgramChecklistPreview.jsx';
import {
  GrantEligibilityPanel,
  GrantEligibilityDetailsPanel,
  CompanyContextPanel,
} from './opportunityContext/eligibilityContext.jsx';

const modalSelectStyles = buildOpportunityModalSelectStyles();
const assigneeSelectStyles = buildOpportunityModalSelectStyles({
  menuMinWidth: ASSIGNEE_MENU_MIN_WIDTH,
});
const statusSelectStyles = buildOpportunityStatusSelectStyles();

const formatStatusOptionLabel = formatOpportunityStatusOptionLabel;
const formatStatusGroupLabel = formatOpportunityStatusGroupLabel;

const findAssigneeOption = (options, value) => {
  if (!value) return null;
  for (const group of options) {
    const match = group.options?.find((option) => option.value === value);
    if (match) return match;
  }
  return { value, label: value };
};

const addDaysToDateString = (dateString, days) => {
  if (!dateString) return '';
  const date = new Date(`${dateString}T12:00:00`);
  if (isNaN(date.getTime())) return '';
  date.setDate(date.getDate() + days);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseAmountInput = (value) => {
  if (value === null || value === undefined || value === '') return '';
  return String(value).replace(/[^\d]/g, '');
};

const formatAmountInput = (value) => {
  const numeric = parseAmountInput(value);
  if (!numeric) return '';
  const num = Number(numeric);
  if (isNaN(num)) return '';
  return formatCurrencyValue(num) || `$${num.toLocaleString()}`;
};

const CurrencyAmountInput = ({
  value,
  onChange,
  onKeyDown,
  className,
  placeholder,
  required,
}) => {
  const [displayValue, setDisplayValue] = useState(() => formatAmountInput(value));

  useEffect(() => {
    const propNumeric = parseAmountInput(value);
    const displayNumeric = parseAmountInput(displayValue);
    if (propNumeric !== displayNumeric) {
      setDisplayValue(formatAmountInput(value));
    }
  }, [value, displayValue]);

  const handleChange = (event) => {
    const parsed = parseAmountInput(event.target.value);
    setDisplayValue(formatAmountInput(parsed));
    onChange(parsed);
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      value={displayValue}
      onChange={handleChange}
      onKeyDown={onKeyDown}
      className={className}
      placeholder={placeholder}
      required={required}
    />
  );
};

const DEFAULT_GRANT_REFERENCE_DATA = { countryCodes, regionCodes, industries };

const GrantOpportunityModal = ({
  isOpen,
  onClose,
  mode = 'add', // 'add' or 'edit'
  opportunity = null, // For edit mode
  allGrants = [],
  allCompanies = [], // Add companies prop
  onSave,
  currencyFormat,
  isAdmin = false,
  selectedGrant = null,
  selectedCompany = null, // Add selected company prop
  formData = {},
  onFormDataChange,
  onSelectedGrantChange,
  onSelectedCompanyChange, // Add company change handler
  isAllCompaniesMode = false, // Add prop to determine if we're in All Opportunities mode
  mitacsInstitutions = [],
  referenceData = {},
  onBulkSave,
  statusOptions = [
    { value: 'Value Roadmap', label: 'Value Roadmap' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'Completed', label: 'Completed' },
    { value: 'Not Started', label: 'Not Started' },
    { value: 'Archived', label: 'Archived' }
  ]
}) => {
  const [addMode, setAddMode] = useState('manual'); // 'manual' | 'resume'
  const [resumeFocusedGrantId, setResumeFocusedGrantId] = useState(null);
  const [loadedMitacsInstitutions, setLoadedMitacsInstitutions] = useState([]);
  const { user, isLoaded: isUserLoaded } = useUser();

  const effectiveMitacsInstitutions = useMemo(
    () => (mitacsInstitutions.length > 0 ? mitacsInstitutions : loadedMitacsInstitutions),
    [mitacsInstitutions, loadedMitacsInstitutions]
  );

  const effectiveReferenceData = useMemo(
    () => (referenceData?.countryCodes ? referenceData : DEFAULT_GRANT_REFERENCE_DATA),
    [referenceData]
  );

  useEffect(() => {
    if (!isOpen) {
      setAddMode('manual');
      setResumeFocusedGrantId(null);
      setLoadedMitacsInstitutions([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || mode !== 'add' || addMode !== 'resume' || mitacsInstitutions.length > 0) {
      return undefined;
    }

    let cancelled = false;

    fetchMitacsInstitutions()
      .then((records) => {
        if (!cancelled) {
          setLoadedMitacsInstitutions(records);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch Mitacs institutions:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, mode, addMode, mitacsInstitutions.length]);

  useEffect(() => {
    if (!isOpen || mode !== 'add' || !isUserLoaded || formData.assignee) return;
    const userEmail = user?.primaryEmailAddress?.emailAddress;
    if (!userEmail || !onFormDataChange) return;
    onFormDataChange('assignee', userEmail);
  }, [isOpen, mode, isUserLoaded, user, formData.assignee, onFormDataChange]);

  const assigneeOptions = useMemo(
    () => buildAssigneeSelectOptions(OPPORTUNITY_ASSIGNEES),
    []
  );
  const selectedAssigneeOption = useMemo(
    () => findAssigneeOption(assigneeOptions, formData.assignee),
    [assigneeOptions, formData.assignee]
  );

  const coloredStatusOptions = useMemo(
    () => mapStatusOptionsWithColors(statusOptions),
    [statusOptions]
  );

  const groupedStatusOptions = useMemo(
    () => mapStatusOptionsGroupedByColor(statusOptions),
    [statusOptions]
  );

  const selectedStatusOption = useMemo(
    () =>
      coloredStatusOptions.find((option) => option.value === formData.status) ||
      coloredStatusOptions.find((option) => option.value === 'Value Roadmap') ||
      null,
    [coloredStatusOptions, formData.status]
  );

  const effectiveCompany = useMemo(() => {
    if (selectedCompany) return selectedCompany;
    if (!isAllCompaniesMode && allCompanies.length === 1) return allCompanies[0];
    return null;
  }, [selectedCompany, isAllCompaniesMode, allCompanies]);

  const showContextSidebar = selectedGrant !== 'vendor';

  const resumeFocusedGrant = useMemo(() => {
    if (!resumeFocusedGrantId) return null;
    return allGrants.find((grant) => grant.id === resumeFocusedGrantId) || null;
  }, [resumeFocusedGrantId, allGrants]);

  const contextSidebarGrant =
    mode === 'add' && addMode === 'resume' ? resumeFocusedGrant : selectedGrant;

  // Use props directly instead of internal state
  const updateFormField = (field, value) => {
    if (onFormDataChange) {
      onFormDataChange(field, value);
    }
  };

  const handleEstCompletionDateChange = (value) => {
    if (!onFormDataChange) return;
    if (value) {
      onFormDataChange({
        estCompletionDate: value,
        estHearBackDate: addDaysToDateString(value, 90),
      });
      return;
    }
    onFormDataChange('estCompletionDate', value);
  };

  const handleGrantSelection = (option) => {
    if (onSelectedGrantChange) {
      onSelectedGrantChange(option?.value);
    }
    
    // In edit mode, we want to update the grant details display but preserve form data
    // The parent component will handle the logic for when to update form data
  };

  const handleCompanySelection = (option) => {
    if (onSelectedCompanyChange) {
      onSelectedCompanyChange(option?.value);
    }
  };

  const handleSave = () => {
    debugLog('🔵 handleSave called - isFormValid:', isFormValid(), 'formData:', formData);
    if (onSave) {
      debugLog('🔵 Calling onSave function');
      onSave();
    } else {
      debugLog('🔴 onSave function is not provided');
    }
  };

  const handleClose = () => {
    debugLog('🔴 Modal closing triggered - isFormValid:', isFormValid(), 'formData:', formData, 'selectedCompany:', selectedCompany);
    debugLog('🔴 Stack trace:', new Error().stack);
    if (onClose) {
      onClose();
    }
  };

  // Handle Enter key press to prevent form submission
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isFormValid()) {
        handleSave();
      }
    }
  };

  const isFormValid = () => {
    debugLog('🔵 isFormValid check - mode:', mode, 'formData:', formData, 'selectedCompany:', selectedCompany, 'isAllCompaniesMode:', isAllCompaniesMode);
    if (mode === 'edit') {
      // In edit mode, company is only required if we're in All Opportunities mode
      const companyValid = isAllCompaniesMode ? selectedCompany : true;
      const isValid = formData.assignee && formData.opportunityName && formData.amount && companyValid;
      debugLog('🔵 Edit mode validation:', { assignee: !!formData.assignee, opportunityName: !!formData.opportunityName, amount: !!formData.amount, companyValid, isValid });
      return isValid;
    }
    if (selectedGrant === 'other') {
      // For custom grants, company is only required if we're in All Opportunities mode
      const companyValid = isAllCompaniesMode ? (selectedCompany || formData.companyID) : true;
      return (
        formData.assignee &&
        (selectedGrant || (formData.opportunityName && formData.amount && companyValid))
      );
    }
    // For regular grants, company is only required if we're in All Opportunities mode
    const companyValid = isAllCompaniesMode ? (selectedCompany || formData.companyID) : true;
    return formData.assignee && formData.opportunityName && formData.amount && companyValid;
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        {debugLog('Modal rendering - isOpen:', isOpen, 'formData:', formData, 'selectedCompany:', selectedCompany)}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-75" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel 
                className="w-full min-h-[500px] max-w-6xl transform overflow-hidden rounded-2xl bg-white p-8 text-left align-middle shadow-xl transition-all"
              >
                <div className="mb-4 flex items-center justify-between gap-4">
                  <Dialog.Title as="h3" className="text-base font-semibold tracking-tight text-gray-900">
                    {mode === 'edit' ? (
                      'Edit Opportunity'
                    ) : selectedGrant === 'vendor' ? (
                      'Add Vendor Savings'
                    ) : (
                      <>
                        Add Opportunity{' '}
                        <span aria-hidden="true">🌴</span>
                      </>
                    )}
                  </Dialog.Title>

                  {mode === 'add' && selectedGrant !== 'vendor' && (
                    <div className="inline-flex shrink-0 gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1">
                      <button
                        type="button"
                        onClick={() => setAddMode('manual')}
                        className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                          addMode === 'manual'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        Manual
                      </button>
                      <button
                        type="button"
                        onClick={() => setAddMode('resume')}
                        className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                          addMode === 'resume'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        Qualify by Resume (BETA)
                      </button>
                    </div>
                  )}
                </div>

                {mode === 'add' && addMode === 'resume' && selectedGrant !== 'vendor' ? (
                  <div className={`grid gap-6 ${showContextSidebar ? 'lg:grid-cols-3' : ''}`}>
                    <div className={showContextSidebar ? 'lg:col-span-2' : ''}>
                      <ResumeQualificationPanel
                        allGrants={allGrants}
                        allCompanies={allCompanies}
                        mitacsInstitutions={effectiveMitacsInstitutions}
                        referenceData={effectiveReferenceData}
                        selectedCompany={selectedCompany}
                        onSelectedCompanyChange={onSelectedCompanyChange}
                        isAllCompaniesMode={isAllCompaniesMode}
                        formData={formData}
                        onFormDataChange={onFormDataChange}
                        statusOptions={statusOptions}
                        currencyFormat={currencyFormat}
                        onBulkSave={onBulkSave}
                        onClose={handleClose}
                        focusedGrantId={resumeFocusedGrantId}
                        onFocusedGrantChange={setResumeFocusedGrantId}
                      />
                    </div>

                    {showContextSidebar && (
                      <aside className="flex flex-col gap-3 lg:col-span-1">
                        <div className="grid grid-cols-1 gap-3">
                          <div className="rounded-xl border border-gray-100 bg-white p-3">
                            <h4 className="mb-1.5 text-xs font-semibold text-gray-900">
                              Grant Eligibility
                            </h4>
                            <GrantEligibilityPanel
                              grant={contextSidebarGrant}
                              currencyFormat={currencyFormat}
                            />
                          </div>

                          <div className="rounded-xl border border-gray-100 bg-white p-3">
                            <h4 className="mb-1.5 text-xs font-semibold text-gray-900">
                              Company Profile
                            </h4>
                            <CompanyContextPanel
                              company={effectiveCompany}
                              currencyFormat={currencyFormat}
                            />
                          </div>
                        </div>

                        <GrantEligibilityDetailsPanel grant={contextSidebarGrant} />

                        {contextSidebarGrant &&
                          contextSidebarGrant !== 'other' &&
                          contextSidebarGrant !== 'vendor' && (
                            <div className="rounded-xl border border-gray-100 bg-white p-3">
                              <h4 className="mb-1.5 text-xs font-semibold text-gray-900">
                                Program checklist
                              </h4>
                              <ProgramChecklistPreview
                                grant={contextSidebarGrant}
                                compact
                              />
                            </div>
                          )}
                      </aside>
                    )}
                  </div>
                ) : (
                <>
                <div className={`grid gap-6 ${showContextSidebar ? 'lg:grid-cols-7' : ''}`}>
                  <div className={`space-y-4 ${showContextSidebar ? 'lg:col-span-3' : ''}`}>
                  {/* Grant Search (available for both add and edit modes) */}
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">
                      {selectedGrant === 'vendor' ? 'Search Vendor Savings' : 'Search Grants'}
                    </label>
                    <Select
                      className="w-full"
                      classNamePrefix="grant-select"
                      options={[
                        ...allGrants.map(grant => ({
                          value: grant,
                          label: grant.fields?.Name || 'Unknown',
                          amount: grant.fields?.AnnualMargin || grant.fields?.Amount,
                          deadline: grant.fields?.Deadline
                        })),
                        { value: 'vendor', label: 'Vendor Savings', amount: null, deadline: null },
                        { value: 'other', label: 'Other (Custom Grant)', amount: null, deadline: null }
                      ]}
                      value={selectedGrant ? 
                        (selectedGrant === 'other' ? 
                          { value: 'other', label: 'Other (Custom Grant)', amount: null, deadline: null } :
                          selectedGrant === 'vendor' ?
                          { value: 'vendor', label: 'Vendor Savings', amount: null, deadline: null } :
                          {
                            value: selectedGrant,
                            label: selectedGrant.fields?.Name || 'Unknown',
                            amount: selectedGrant.fields?.AnnualMargin || selectedGrant.fields?.Amount,
                            deadline: selectedGrant.fields?.Deadline
                          }
                        ) : null}
                      onChange={handleGrantSelection}
                      formatOptionLabel={({ label, amount, deadline, value }) => (
                        <div className="flex flex-col">
                          <div className="font-medium">{label}</div>
                          {value !== 'other' && value !== 'vendor' && (
                            <div className="text-xs text-gray-500">
                              {amount ? `${currencyFormat(amount)}/yr` : 'No amount specified'} •
                              {deadline ? ` Deadline: ${deadline}` : ' No deadline'}
                            </div>
                          )}
                        </div>
                      )}
                      placeholder={selectedGrant === 'vendor' ? "Search for vendor savings..." : "Search for a grant..."}
                      isClearable
                      isSearchable
                      styles={modalSelectStyles}
                      {...MODAL_SELECT_PROPS}
                    />
                  </div>

                  {/* Company Selection - Only show in All Opportunities mode */}
                  {isAllCompaniesMode && (
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-700">
                        Company <span className="text-red-500">*</span>
                      </label>
                      <Select
                        className="w-full"
                        classNamePrefix="company-select"
                        options={allCompanies.map(company => ({
                          value: company,
                          label: company.fields.Name || company.fields.CompanyName || 'Unnamed Company',
                          id: company.id
                        }))}
                        value={selectedCompany ? {
                          value: selectedCompany,
                          label: selectedCompany.fields.Name || selectedCompany.fields.CompanyName || 'Unnamed Company',
                          id: selectedCompany.id
                        } : null}
                        onChange={handleCompanySelection}
                        placeholder="Select a company..."
                        isClearable
                        isSearchable
                        styles={modalSelectStyles}
                        {...MODAL_SELECT_PROPS}
                      />
                    </div>
                  )}

                  {/* Grant Configuration */}
                  {(selectedGrant || mode === 'edit') && (
                    <div className="mt-4 space-y-4">
                      <h4 className="text-xs font-semibold text-gray-900">
                        {mode === 'edit' ? 'Opportunity Configuration' : 'Grant Configuration'}
                      </h4>

                      {/* Assignee + Status */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Assignee <span className="text-red-500">*</span>
                            </label>
                            <Select
                              isClearable
                              className="w-full"
                              classNamePrefix="assignee-select"
                              placeholder="Select assignee…"
                              value={selectedAssigneeOption}
                              onChange={(option) =>
                                updateFormField('assignee', option ? option.value : '')
                              }
                              options={assigneeOptions}
                              isSearchable
                              styles={assigneeSelectStyles}
                              noOptionsMessage={() => 'No assignees found'}
                              required
                              {...MODAL_SELECT_PROPS}
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Status <span className="text-red-500">*</span>
                            </label>
                            <Select
                              className="w-full"
                              classNamePrefix="status-select"
                              options={groupedStatusOptions}
                              value={selectedStatusOption}
                              onChange={(option) =>
                                updateFormField('status', option ? option.value : 'Value Roadmap')
                              }
                              formatOptionLabel={formatStatusOptionLabel}
                              formatGroupLabel={formatStatusGroupLabel}
                              isSearchable
                              styles={statusSelectStyles}
                              {...MODAL_SELECT_PROPS}
                            />
                          </div>
                        </div>

                        {/* Opportunity Name + Amount */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Opportunity Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.opportunityName || ''}
                              onChange={(e) => updateFormField('opportunityName', e.target.value)}
                              onKeyDown={handleKeyDown}
                              className="w-full rounded border-gray-300 focus:ring-2 focus:ring-blue-400 px-3 py-2 text-sm"
                              placeholder={selectedGrant === 'other' ? "Enter custom grant name..." : "Grant Name [New]"}
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Amount (Adjusted) <span className="text-red-500">*</span>
                            </label>
                            <CurrencyAmountInput
                              value={formData.amount || ''}
                              onChange={(value) => updateFormField('amount', value)}
                              onKeyDown={handleKeyDown}
                              className="w-full rounded border-gray-300 focus:ring-2 focus:ring-blue-400 px-3 py-2 text-sm"
                              placeholder="$50,000"
                              required
                            />
                          </div>
                        </div>

                        {/* Est. Completion / Hear Back dates */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Est. Completion Date <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="date"
                              value={formData.estCompletionDate || ''}
                              onChange={(e) =>
                                handleEstCompletionDateChange(e.target.value)
                              }
                              onKeyDown={handleKeyDown}
                              className="w-full rounded border-gray-300 focus:ring-2 focus:ring-blue-400 px-3 py-2 text-sm"
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Est. Hear Back Date <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="date"
                              value={formData.estHearBackDate || ''}
                              onChange={(e) =>
                                updateFormField('estHearBackDate', e.target.value)
                              }
                              onKeyDown={handleKeyDown}
                              className="w-full rounded border-gray-300 focus:ring-2 focus:ring-blue-400 px-3 py-2 text-sm"
                              required
                            />
                          </div>
                        </div>

                        {/* Notes */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Notes
                          </label>
                          <textarea
                            value={formData.notes || ''}
                            onChange={(e) => updateFormField('notes', e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="w-full rounded border-gray-300 focus:ring-2 focus:ring-blue-400 px-3 py-2 text-sm h-20 resize-none"
                            placeholder="Add notes about this grant opportunity..."
                          />
                        </div>
                    </div>
                  )}
                  </div>

                  {showContextSidebar && (
                    <aside className="flex flex-col gap-3 lg:col-span-4">
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="rounded-xl border border-gray-100 bg-white p-3">
                          <h4 className="mb-1.5 text-xs font-semibold text-gray-900">
                            Grant Eligibility
                          </h4>
                          <GrantEligibilityPanel
                            grant={contextSidebarGrant}
                            currencyFormat={currencyFormat}
                          />
                        </div>

                        <div className="rounded-xl border border-gray-100 bg-white p-3">
                          <h4 className="mb-1.5 text-xs font-semibold text-gray-900">
                            Company Profile
                          </h4>
                          <CompanyContextPanel
                            company={effectiveCompany}
                            currencyFormat={currencyFormat}
                          />
                        </div>
                      </div>

                      <GrantEligibilityDetailsPanel grant={contextSidebarGrant} />

                      {contextSidebarGrant &&
                        contextSidebarGrant !== 'other' &&
                        contextSidebarGrant !== 'vendor' && (
                          <div className="rounded-xl border border-gray-100 bg-white p-3">
                            <h4 className="mb-1.5 text-xs font-semibold text-gray-900">
                              Program checklist
                            </h4>
                            <ProgramChecklistPreview
                              grant={contextSidebarGrant}
                              compact
                            />
                          </div>
                        )}
                    </aside>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 justify-end mt-6">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={!isFormValid()}
                    className="inline-flex items-center rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {mode === 'edit' ? 'Save Changes' : 
                     (selectedGrant === 'vendor' ? 'Add Vendor Savings' : 
                      (selectedGrant === 'other' ? 'Add Custom Grant' : 'Add Opportunity'))}
                  </button>
                </div>
                </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default GrantOpportunityModal; 